import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'

import ConfirmPopup from '../../../../../../../components/Popups/ConfirmPopup'
import PaginationFooter from '../../../../../../../components/PaginationFooter/PaginationFooter'
import { selectActiveBuilding } from '../../../../../../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../../../../../../reducers/userDataReducer'
import ChecklistsUpdateCustomTaskModal from '../../../../ChecklistsCreateCustomTaskModal/ChecklistsUpdateCustomTaskModal'
import {
  fetchCustomTasks,
  selectCustomTasks,
  removeCustomTask,
  duplicateCustomTask,
} from '../../../../../../../reducers/Organisation/checklistsReducer'
import { useSnackbar } from '../../../../../../../helpers/hooks/useSnackbar'

import CustomTasksTable from './CustomTasksTable'
import CustomTasksNavbar from './CustomTasksNavbar'

const CustomTasks = ({
  tasks,
  userRole,
  fetchCustomTasks = () => {},
  removeCustomTask = () => {},
  duplicateCustomTask = () => {},
}) => {
  const { t } = useTranslation(['checklists-settings', 'common'])
  const [isSiteLoading, setSiteLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState(null)
  const [editedTask, setEditedTask] = useState(null)
  const [removeTask, setRemoveTask] = useState(null)
  const [prevSearchValue, setPrevSearchValue] = useState('')

  const { displaySnackbar } = useSnackbar()

  const selectedBuilding = useSelector(selectActiveBuilding)

  const fetchTasks = async (query) => {
    setSiteLoading(true)
    const requestData = {
      buildingId: selectedBuilding.id,
      page: query ? 1 : pageNumber,
      limit: showOnPage,
      type: (selectedTab && selectedTab.value) || undefined,
      search: query || search,
    }
    await fetchCustomTasks(requestData)
      .then((count) => setPaginationItemsCount(count))
      .catch((error) => console.error(error.message))
    setSiteLoading(false)
  }

  const debounceFn = debounce(fetchTasks, 1000)

  const onDuplicate = async (task) => {
    if (!selectedBuilding?.id || task?.id) return

    try {
      await duplicateCustomTask(selectedBuilding.id, task.id)
      displaySnackbar(t('checklists-settings:customTasks.duplicateSuccess', { name: task.name }))
    } catch (error) {
      console.error(error.message)
      displaySnackbar(t('checklists-settings:customTasks.duplicateError'), true)
    }
  }

  const onDeleteFinally = () => {
    fetchTasks()
    setRemoveTask(null)
  }

  const onDeleteTask = async () => {
    if (!removeTask?.id) return

    await removeCustomTask(removeTask.id)
      .then(() => {
        displaySnackbar(t('checklists-settings:customTasks.deleteSuccess'))
      })
      .catch(({ message }) => {
        console.error(message)
        displaySnackbar(t('checklists-settings:customTasks.deleteError'), true)
      })
      .finally(onDeleteFinally)
  }

  useEffect(() => {
    if (prevSearchValue !== search) {
      debounceFn(search)
    }
    setPrevSearchValue(search)
    return () => {
      debounceFn.cancel()
    }
  }, [search])

  useEffect(() => {
    if (selectedBuilding) {
      fetchTasks()
    }
  }, [pageNumber, showOnPage, selectedTab, selectedBuilding])

  return (
    <React.Fragment>
      <div className="ctp-checklists-wrapper ctp-checklists-custom-tasks">
        <div className={`ctp-checklists-wrapper__inner ${isSiteLoading ? 'is-loading' : ''}`}>
          <CustomTasksNavbar
            search={search}
            selectedTab={selectedTab}
            setSearch={(e) => {
              setSearch(e)
              setPageNumber(1)
            }}
            setSelectedTab={(tab) => {
              setSelectedTab(tab)
              setPageNumber(1)
            }}
          />
          <div className={`ctp-checklists-wrapper-main-table-and-footer-container`}>
            <CustomTasksTable
              onEdit={(task) => setEditedTask(task)}
              onDuplicate={onDuplicate}
              onDelete={(task) => setRemoveTask(task)}
              selectedBuilding={selectedBuilding}
              data={tasks}
              userRole={userRole}
            />
            {tasks && tasks.length ? (
              <PaginationFooter
                showOnPage={showOnPage}
                pageNumber={pageNumber}
                paginationItemsCount={paginationItemsCount}
                setSiteLoading={setSiteLoading}
                setShowOnPage={setShowOnPage}
                setPageNumber={setPageNumber}
              />
            ) : null}
          </div>
        </div>
      </div>
      {editedTask && (
        <ChecklistsUpdateCustomTaskModal
          onClose={() => setEditedTask(null)}
          selectedBuilding={selectedBuilding}
          taskId={editedTask.id}
        />
      )}
      {removeTask && (
        <ConfirmPopup
          title={t('checklists-settings:manageInspection.confirmationTitle')}
          body={t('checklists-settings:customTasks.deleteConfirmBody', { name: removeTask.name })}
          cancelText={t('common:actions.cancel')}
          confirmText={t('checklists-settings:customTasks.deleteTaskConfirmButton')}
          isOpen={!!removeTask}
          cancelCallback={() => setRemoveTask(null)}
          confirmCallback={onDeleteTask}
        />
      )}
    </React.Fragment>
  )
}

CustomTasks.propTypes = {
  tasks: PropTypes.array,
  userRole: PropTypes.string,
  fetchCustomTasks: PropTypes.func,
  removeCustomTask: PropTypes.func,
  duplicateCustomTask: PropTypes.func,
}

export default connect(
  (state) => ({
    tasks: selectCustomTasks(state),
    userRole: selectUserRole(state),
  }),
  {
    fetchCustomTasks,
    removeCustomTask,
    duplicateCustomTask,
  },
)(CustomTasks)
